/** Selectors for the watermark library (VE-24693). */
import { createSelector } from '@reduxjs/toolkit';
import { defaultState, namespace, RedactWatermarksState } from '../store';
import { watermarkAppliesTo } from '../models';

/**
 * Falls back to `defaultState` when the slice is absent. The reducer is mounted
 * by `configureStore`, so at runtime it is always there — but consumers of this
 * module render against partial stores too (component tests build state by
 * hand), and MultiSelectBar reads the library on every folder-view render. An
 * unguarded lookup turned "this store has no watermarks" into a render crash of
 * the whole folder view.
 */
const slice = (state: RedactWatermarksState) =>
  state[namespace] ?? defaultState;

/**
 * Every record the store holds, deleted ones included. Raw — prefer one of the
 * three filtered selectors below, which encode who is allowed to see what.
 */
export const selectAllWatermarkRecords = (state: RedactWatermarksState) =>
  slice(state).watermarkList;

/**
 * The library as the settings table shows it: everything not soft-deleted,
 * whether or not it is enabled. A disabled watermark still has to be visible
 * there, because that page is where it gets re-enabled.
 */
export const selectWatermarkList = createSelector(
  selectAllWatermarkRecords,
  (list) => list.filter((w) => w.data.isDeleted !== true)
);

/** The deleted view. */
export const selectDeletedWatermarkList = createSelector(
  selectAllWatermarkRecords,
  (list) => list.filter((w) => w.data.isDeleted === true)
);

/**
 * What a PICKER may offer — the editor panel and the folder-view batch bar.
 *
 * All FOUR exclusions live here rather than at each picker, because "hidden
 * everywhere" is the whole meaning of them and there are two pickers today with
 * a third likely. `enabled` is absent on records written before the field
 * existed, and absent must read as enabled or every existing library would
 * empty out on upgrade. `appliesToDocuments` follows the same absent-means-yes
 * rule, for the same reason and by the same helper — both pickers this feeds
 * put marks on documents.
 *
 * The last is an image definition with no image. `vetRecord` nulls artwork this
 * client refuses — a JPEG written by another client, a value over the size cap,
 * a row poisoned through the public API — and applying such a definition is a
 * silent no-op: the mark carries no bytes, the overlay draws nothing, and
 * `toBurnInWatermark` drops it. The operator gets a document they believe is
 * watermarked and is not, which is the one outcome this feature must not have.
 * Better to not offer it.
 *
 * "Has an image" is NOT "has an inline raster" any more (§K). Artwork normally
 * lives on the watermark's own TDO, so an inline-only test hid every watermark
 * created since that move from both pickers — the settings page listed them,
 * the editor dropdown did not, and nothing said why. Any of the three is
 * sufficient: bytes in hand, a TDO to fetch them from, or a preview (which only
 * a row with artwork ever gets).
 */
export const selectPickableWatermarks = createSelector(
  selectWatermarkList,
  (list) =>
    list.filter(
      (w) =>
        w.data.enabled !== false &&
        // Both pickers this feeds apply to DOCUMENTS, so this is the reader of
        // `appliesToDocuments`. Absent counts as true (see `watermarkAppliesTo`),
        // which is why the check is safe to add to a library that already has
        // rows: it excludes only a row that says false, and nothing says false
        // today. When images and videos are implemented, the kind becomes an
        // argument here rather than a second copy of this filter.
        watermarkAppliesTo(w.data, 'documents') &&
        (w.data.type !== 'image' ||
          Boolean(
            w.data.imageBaseDataUrl ??
              w.data.imageTdoId ??
              w.data.previewDataUrl
          ))
    )
);

export const selectIsWatermarkDrawerOpen = (state: RedactWatermarksState) =>
  slice(state).isDrawerOpen;

export const selectEditingWatermarkId = (state: RedactWatermarksState) =>
  slice(state).editingWatermarkId;

export const selectEditingWatermark = createSelector(
  selectWatermarkList,
  selectEditingWatermarkId,
  (list, editingId) =>
    editingId ? list.find((w) => w.id === editingId) : undefined
);

export const selectDeletingWatermarkId = (state: RedactWatermarksState) =>
  slice(state).deletingWatermarkId;

/** Whether the open confirmation is the irreversible one. */
export const selectIsDeletePermanent = (state: RedactWatermarksState) =>
  slice(state).isDeletePermanent;

export const selectIsWatermarkFetchInProgress = (
  state: RedactWatermarksState
) => slice(state).isFetchInProgress;

export const selectIsWatermarkSaveInProgress = (state: RedactWatermarksState) =>
  slice(state).isSaveInProgress;

export const selectIsWatermarkDeleteInProgress = (
  state: RedactWatermarksState
) => slice(state).isDeleteInProgress;

/**
 * True once a CRUD attempt has proved the registry has no published schema.
 * Undefined-safe: before the first fetch this is `false`, so the SDO path is
 * tried rather than pre-emptively written off.
 */
export const selectWatermarkRegistryUnavailable = (
  state: RedactWatermarksState
) => slice(state).registryUnavailable === true;

/**
 * Whether a fetch has completed, so an empty list means an empty LIBRARY.
 * Guards the table's empty state, which otherwise flashes on every load.
 */
export const selectHasFetchedWatermarks = (state: RedactWatermarksState) =>
  slice(state).hasFetched;

const EMPTY_IDS: readonly string[] = [];

/**
 * Definitions still being applied to the open document (VE-32397). The
 * fallback is for hand-built slices in component tests, as with `slice`.
 */
export const selectApplyingWatermarkIds = (
  state: RedactWatermarksState
): readonly string[] => slice(state).applyingWatermarkIds ?? EMPTY_IDS;

/** Whether the settings page is showing the deleted view. */
export const selectShowDeletedWatermarks = (state: RedactWatermarksState) =>
  slice(state).showDeleted;
